/**
 * 69.2c, the web (lane D; decision 145 (1): one web; DIRECTION §0.8): a side's structures linked back to its HQ.
 *
 * The nodes are the side's standing HQs (the `ecotourism` body), relays, beacons, landing pads and (69.4) forward bases, and — under an
 * Operations push or the tug of war (69.6d / 69.6e) — the objectives it holds, the sector chain that is the web's first,
 * linear form (146). Two of a side's nodes on one planet link when they stand within `relayLink(state)` of each other
 * along the ground; whatever a chain of links reaches from an HQ is linked. Intel (a relay sees only while linked:
 * core/relay.ts `relaySees`), arrivals (69.3a's drops, core/drops.ts) and later supply and strategy read it.
 *
 * Derived, never edited: the Match works each side's web out once a second at a fixed tick (match/rates.ts `RATES.web`)
 * into `state.webs`, and `isLinked` reads that; before the first pass it walks the chain itself, so it answers the same at
 * any tick. Nothing runs, and nothing is stored, without GameConfig.relays. Pure.
 *
 * Cost: n nodes a side, n²/2 arc tests once a second — tens of nodes on Foothold's board, microseconds.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { relayLink, relaySees } from './relay';
import { structureTeam } from './body';
import { vecDot, vecNormalize } from '../math';
import { dacos } from './dmath';

/** The structure kinds that are nodes of a side's web (an HQ is also a root). */
const NODE_KINDS: ReadonlySet<StructureState['type']> = new Set(['ecotourism', 'relay', 'beacon', 'landingPad', 'fob']);   // 69.4: the forward base

/** A node: a structure (its id) or, under Operations, a held objective (no id), where it stands. */
interface WebNode { id: number | null; root: boolean; planetIndex: number; dir: Vec3 }

function nodesOf(state: GameState, team: number): WebNode[] {
  const out: WebNode[] = [];
  for (const s of state.structures) {
    if (s.health <= 0 || !NODE_KINDS.has(s.type) || structureTeam(state, s) !== team) continue;
    out.push({ id: s.id, root: s.type === 'ecotourism', planetIndex: s.planetIndex, dir: vecNormalize(s.surfaceDir) });
  }
  const pts = state.modeState.points;
  if (pts?.front && pts.push) for (const p of pts.sites) if (p.owner === team) out.push({ id: null, root: false, planetIndex: p.planetIndex, dir: vecNormalize(p.surfaceDir) });
  return out;
}

/** A side's nodes, the link test between two, and which nodes a chain reaches from an HQ. */
/**
 * A side's nodes, its links, and which nodes a chain reaches from an HQ.
 *
 * DECISIONS 162 (the owner: "Wiring grid … is a little too densely connected, should limit to adjacent connections"; "I think
 * probably the simulated web should be sparse too"): two nodes within link range link only when they are **relative
 * neighbours** — no third node of the side stands closer to both (the relative-neighbourhood graph over the standing nodes).
 * That keeps the links a person would draw, and makes a chain a chain. It is re-derived over the nodes standing now, so
 * a node lost mid-chain lets its neighbours link straight past it when they are within range of each other, and strands
 * what lies behind it only when they are not. GameConfig.denseWeb keeps the earlier rule (every pair within range) for
 * one comparison in play.
 */
function walk(state: GameState, team: number): { nodes: WebNode[]; edges: Array<[number, number]>; seen: boolean[] } {
  const nodes = nodesOf(state, team);
  const reach = relayLink(state);
  const n = nodes.length;
  // the distances along the ground, once (Infinity across planets)
  const d: number[][] = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
    const a = nodes[i], b = nodes[j];
    const x = a.planetIndex !== b.planetIndex ? Infinity : dacos(Math.max(-1, Math.min(1, vecDot(a.dir, b.dir)))) * (state.planets[a.planetIndex]?.radius ?? 0);
    d[i][j] = d[j][i] = x;
  }
  const edges: Array<[number, number]> = [];
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
    if (d[i][j] > reach) continue;
    if (!state.config.denseWeb) {
      let blocked = false;
      for (let k = 0; k < n && !blocked; k++) if (k !== i && k !== j && Math.max(d[i][k], d[j][k]) < d[i][j]) blocked = true;
      if (blocked) continue;
    }
    edges.push([i, j]);
  }
  const adjacent: number[][] = Array.from({ length: n }, () => []);
  for (const [i, j] of edges) { adjacent[i].push(j); adjacent[j].push(i); }
  const seen = new Array<boolean>(n).fill(false);
  const queue: number[] = [];
  nodes.forEach((nd, i) => { if (nd.root) { seen[i] = true; queue.push(i); } });
  while (queue.length) {
    const i = queue.shift()!;
    for (const j of adjacent[i]) if (!seen[j]) { seen[j] = true; queue.push(j); }
  }
  return { nodes, edges, seen };
}

/** The ids of a side's structures its web links to an HQ, worked out now (ascending). */
export function linkedIds(state: GameState, team: number): number[] {
  const { nodes, seen } = walk(state, team);
  return nodes.filter((n, i) => seen[i] && n.id !== null).map(n => n.id!).sort((a, b) => a - b);
}

/**
 * 69.4 step 2b: how far along the web each of a side's linked structures stands from its HQ — the shortest way along the
 * links (each link its length along the ground), so "nearest the front" is the one the web reaches farthest out. Keyed by
 * structure id; the unlinked are absent. Ties and order are by node order, so it is deterministic.
 */
export function webDistances(state: GameState, team: number): Map<number, number> {
  const { nodes, edges, seen } = walk(state, team);
  const n = nodes.length, dist = new Array<number>(n).fill(Infinity), done = new Array<boolean>(n).fill(false);
  const adj: Array<Array<[number, number]>> = Array.from({ length: n }, () => []);
  for (const [i, j] of edges) {
    const a = nodes[i], b = nodes[j];
    const w = dacos(Math.max(-1, Math.min(1, vecDot(a.dir, b.dir)))) * (state.planets[a.planetIndex]?.radius ?? 0);
    adj[i].push([j, w]); adj[j].push([i, w]);
  }
  nodes.forEach((nd, i) => { if (nd.root) dist[i] = 0; });
  for (;;) {
    let u = -1;
    for (let i = 0; i < n; i++) if (!done[i] && dist[i] < Infinity && (u < 0 || dist[i] < dist[u])) u = i;
    if (u < 0) break;
    done[u] = true;
    for (const [v, w] of adj[u]) if (dist[u] + w < dist[v]) dist[v] = dist[u] + w;
  }
  const out = new Map<number, number>();
  nodes.forEach((nd, i) => { if (seen[i] && nd.id !== null && dist[i] < Infinity) out.set(nd.id, dist[i]); });
  return out;
}

/**
 * 69.2d: the web's links as the table draws them — every pair of a side's nodes within link range, `live` when the chain
 * reaches both from an HQ (a dead link joins two nodes stranded together). None without GameConfig.relays.
 */
export function webLinks(state: GameState, team: number): Array<{ planetIndex: number; a: Vec3; b: Vec3; live: boolean }> {
  if (!state.config.relays) return [];
  const { nodes, edges, seen } = walk(state, team);   // 162: the table draws the sim's own links
  return edges.map(([i, j]) => ({ planetIndex: nodes[i].planetIndex, a: nodes[i].dir, b: nodes[j].dir, live: seen[i] && seen[j] }));
}

/** Every side's web worked out now, into the state (`state.webs`). */
export function updateWebs(state: GameState, tick: number): GameState {
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const webs: NonNullable<GameState['webs']> = {};
  for (const t of teams) webs[t] = { linked: linkedIds(state, t), at: tick };
  return { ...state, webs };
}

/**
 * Is this structure linked to its side's HQ? A standing structure of a side whose web reaches it (`state.webs`, else the
 * chain walked now). Without GameConfig.relays: the relay's standing test (`relaySees`), as before the web.
 */
export function isLinked(state: GameState, s: StructureState): boolean {
  if (!state.config.relays) return relaySees(state, s);
  if (s.health <= 0) return false;
  const team = structureTeam(state, s);
  if (team === null) return false;
  const web = state.webs?.[team];
  return (web ? web.linked : linkedIds(state, team)).includes(s.id);
}
